import { defineStore } from 'pinia'
import { store } from '@/store'
import { fetchGenerateFeatureBreakdown, fetchGenerateDrawingAnalysis, fetchGenerateInventiveIdea, fetchWordExtract } from '@/api/writing'
import { fetchWorkflow } from '@/api/analysis'
import { applicationDataMap } from '@/components/Application'
import { downloadFileByPost } from '@/utils'

export const STEP2_TEMPLATE_SELECT_EVENT = 'step2-template-select'

export const useWritingStore = defineStore('writing', {
  state: () => {
    return {
      step2Info: null,
      step2TemplateList: [],
      step2CurrentTemplate: null,
      /** 项目 statusCode，供顶部 Nav 判断可跳转的最大步骤 */
      projectStatusCode: '',
      /** Step2 完成特征拆解+保护策略时可预览 Step3 */
      step3GateReady: false,
      pendingFileFocus: null,
      /** 交底书侧栏（Step3/4 溯源与查看） */
      disclosurePanel: {
        open: false,
        projectId: '',
        sourceRanges: [],
        excerpt: '',
      },
    }
  },
  getters: {
    getStep2Info(state) {
      return state.step2Info
    },
    getStep2TemplateList(state) {
      return state.step2TemplateList
    },
    getStep2CurrentTemplate(state) {
      return state.step2CurrentTemplate
    },
  },
  actions: {
    setStep2Info(info) {
      this.step2Info = info
      if (info?.statusCode !== undefined && info?.statusCode !== null) {
        this.projectStatusCode = String(info.statusCode)
      }
    },
    setProjectStatusCode(code) {
      this.projectStatusCode = code === undefined || code === null ? '' : String(code)
    },
    setStep3GateReady(ready) {
      this.step3GateReady = Boolean(ready)
    },
    setPendingFileFocus(detail) {
      this.pendingFileFocus = detail || null
    },
    consumePendingFileFocus() {
      const detail = this.pendingFileFocus
      this.pendingFileFocus = null
      return detail
    },
    openDisclosurePanel({ projectId, sourceRanges = [], excerpt = '' } = {}) {
      this.disclosurePanel = {
        open: true,
        projectId: String(projectId || ''),
        sourceRanges: Array.isArray(sourceRanges) ? [...sourceRanges] : [],
        excerpt: String(excerpt || '').trim(),
      }
    },
    closeDisclosurePanel() {
      this.disclosurePanel = {
        ...this.disclosurePanel,
        open: false,
      }
    },
    setStep2TemplateList(list = []) {
      this.step2TemplateList = Array.isArray(list) ? list : []
    },
    setStep2CurrentTemplate(template) {
      this.step2CurrentTemplate = template || null
    },
    resetStep2TemplateState() {
      this.step2TemplateList = []
      this.step2CurrentTemplate = null
    },
    exportStepDetailList(id) {
      downloadFileByPost(`/disclosure/disclosure-project/step/export/comp-comm/${id}`)
    },
    workflow(id, path, params, isRepeat) {
      const getWorkflowParams = (componentTypeCode) => {
        return {
          ...params,
          inputs: {
            ...params.inputs,
            componentTypeCode
          },
        }
      }

      const triggerFeatureBreakdown = () => {
        fetchGenerateFeatureBreakdown(id).catch((error) => {
          console.error('feature breakdown generate failed', error)
        })
      }

      const triggerDrawingAnalysis = () => {
        fetchGenerateDrawingAnalysis(id).catch((error) => {
          console.error('drawing analysis failed', error)
        })
      }

      const syncGeneratedComponentResult = (componentTypeCode, result) => {
        if (!result) {
          return
        }
        const docResult = JSON.stringify(result)
        this.setStep2Info({
          ...(this.step2Info || {}),
          step2Component: {
            ...(this.step2Info?.step2Component || {}),
            [componentTypeCode]: {
              ...(this.step2Info?.step2Component?.[componentTypeCode] || {}),
              componentTypeCode,
              docResult,
            },
          },
        })
      }

      const triggerInventiveIdea = () => {
        fetchGenerateInventiveIdea(id)
          .then((result) => {
            syncGeneratedComponentResult(applicationDataMap.InventIdea, result)
          })
          .catch((error) => {
            console.error('inventive idea generate failed', error)
          })
      }

      if (!isRepeat) {
        fetchWordExtract(id, path)
          .then((result) => {
            if (result) {
              triggerFeatureBreakdown()
              triggerDrawingAnalysis()
              triggerInventiveIdea()
            }
          })
          .catch((error) => {
            console.error('word extract failed', error)
          })
      } else {
        triggerFeatureBreakdown()
        triggerDrawingAnalysis()
        triggerInventiveIdea()
      }

      fetchWorkflow(
        getWorkflowParams(applicationDataMap.Popular),
        {
          Authorization: `Bearer ${process.env.VUE_APP_TOKEN_POP}`
        }
      )

      fetchWorkflow(
        getWorkflowParams(applicationDataMap.Terminology),
        {
          Authorization: `Bearer ${process.env.VUE_APP_TOKEN_GLO}`
        }
      )
    }
  },
  persist: true
})

export function useWritingStoreWithOut() {
  return useWritingStore(store)
}
